import React from 'react';
import {
  Modal,
  Space,
  Row,
  Col,
  Tag,
  Button,
  Card,
  Descriptions,
} from 'antd';
import {
  SafetyCertificateOutlined,
  FilePdfOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  DollarOutlined,
  FolderOpenOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

interface LeadClosingDetailModalProps {
  open: boolean;
  onClose: () => void;
  lead: any;
  formatCurrency: (amount: number) => string;
}

export const LeadClosingDetailModal: React.FC<LeadClosingDetailModalProps> = ({
  open,
  onClose,
  lead,
  formatCurrency,
}) => {
  const activeReq = lead?.active_closing_request;

  return (
    <Modal
      title={
        <Space size={10}>
          <SafetyCertificateOutlined style={{ color: '#7BAA18', fontSize: '22px' }} />
          <span style={{ fontSize: '18px', fontWeight: 700 }}>Rincian Transaksi Closing (O K)</span>
          <Tag color="success" style={{ fontWeight: 600, borderRadius: '4px', fontSize: '12px', padding: '2px 8px' }}>
            APPROVED 🎉
          </Tag>
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={[
        <Button
          key="close"
          type="primary"
          onClick={onClose}
          style={{ backgroundColor: '#7BAA18', borderColor: '#7BAA18', minWidth: '120px' }}
        >
          Tutup
        </Button>
      ]}
      width={880}
      style={{ maxWidth: '95vw' }}
    >
      {activeReq && (
        <Space direction="vertical" size={16} style={{ width: '100%', marginTop: 8 }}>
          {/* Header Highlight Card */}
          <div
            style={{
              background: 'linear-gradient(135deg, #f6ffed 0%, #e6f7ff 100%)',
              border: '1.5px solid #b7eb8f',
              borderRadius: '12px',
              padding: '16px 20px',
            }}
          >
            <Row gutter={[16, 12]} align="middle">
              <Col xs={24} sm={15}>
                <div style={{ fontSize: '12px', textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.5px' }}>
                  Nilai Real Transaksi (Omzet Deal)
                </div>
                <div style={{ fontSize: '24px', fontWeight: 800, color: '#274f13', marginTop: 2 }}>
                  {formatCurrency(activeReq.omzet)}
                </div>
                <div style={{ fontSize: '13px', color: '#334155', marginTop: 4 }}>
                  Kawasan: <strong>{lead.property_area?.name || '-'}</strong> • Unit: <strong>{activeReq.purchased_property_detail}</strong>
                </div>
              </Col>
              <Col xs={24} sm={9} style={{ textAlign: 'right' }}>
                {(activeReq.sp_document || lead.sp_document) && (
                  <Button
                    type="primary"
                    icon={<FilePdfOutlined />}
                    href={activeReq.sp_document || lead.sp_document}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ backgroundColor: '#7BAA18', borderColor: '#7BAA18', fontWeight: 600 }}
                  >
                    Buka Dokumen SP (PDF)
                  </Button>
                )}
              </Col>
            </Row>
          </div>

          {/* Informasi Pembeli */}
          <Card
            size="small"
            title={
              <Space>
                <UserOutlined style={{ color: '#1890ff' }} />
                <span style={{ fontWeight: 600 }}>Data Resmi Pembeli (Buyer Info)</span>
              </Space>
            }
            style={{ borderRadius: '8px' }}
          >
            <Descriptions size="small" bordered column={2}>
              <Descriptions.Item label="Nama Pembeli" span={2}>
                <strong>{activeReq.buyer_name}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Hubungan dengan Lead">
                <Tag color="purple">{activeReq.relationship_with_lead || 'Diri Sendiri'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="No. Telepon">
                {activeReq.buyer_phone ? (
                  <a href={`tel:${activeReq.buyer_phone}`}>
                    <PhoneOutlined style={{ marginRight: 4 }} />
                    {activeReq.buyer_phone}
                  </a>
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {activeReq.buyer_email ? (
                  <a href={`mailto:${activeReq.buyer_email}`}>
                    <MailOutlined style={{ marginRight: 4 }} />
                    {activeReq.buyer_email}
                  </a>
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="NIK KTP">
                {activeReq.buyer_nik || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="NPWP">
                {activeReq.buyer_npwp || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Alamat Domisili" span={2}>
                {lead.alamat ? `${lead.alamat}${lead.kabupaten_kota_name ? `, ${lead.kabupaten_kota_name}` : ''}${lead.provinsi_name ? `, ${lead.provinsi_name}` : ''}` : '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Rincian Transaksi & Dokumen Pendukung */}
          <Card
            size="small"
            title={
              <Space>
                <DollarOutlined style={{ color: '#52c41a' }} />
                <span style={{ fontWeight: 600 }}>Rincian Transaksi & Berkas Pendukung</span>
              </Space>
            }
            style={{ borderRadius: '8px' }}
          >
            <Descriptions size="small" bordered column={2}>
              <Descriptions.Item label="Unit Properti Dibeli" span={2}>
                <strong>{activeReq.purchased_property_detail}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Tipe Properti">
                {lead.property_type?.name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Sumber Lead">
                <Tag color="blue">{lead.lead_source?.name || '-'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Dokumen Surat Pesanan (SP)" span={2}>
                {(activeReq.sp_document || lead.sp_document) ? (
                  <Button
                    type="link"
                    icon={<FilePdfOutlined />}
                    href={activeReq.sp_document || lead.sp_document}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ padding: 0, fontWeight: 600, color: '#7BAA18' }}
                  >
                    Buka Dokumen SP Resmi (PDF)
                  </Button>
                ) : (
                  <span style={{ color: '#8c8c8c' }}>Tidak ada dokumen SP.</span>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Berkas Pendukung / Link SPR" span={2}>
                {activeReq.supporting_documents ? (
                  <Button
                    type="link"
                    icon={<FolderOpenOutlined />}
                    href={activeReq.supporting_documents}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ padding: 0 }}
                  >
                    Buka Tautan Berkas Pendukung (Folder Drive)
                  </Button>
                ) : (
                  <span style={{ color: '#8c8c8c' }}>Tidak ada tautan berkas pendukung.</span>
                )}
              </Descriptions.Item>
              {activeReq.notes && (
                <Descriptions.Item label="Catatan Sales" span={2}>
                  <span style={{ whiteSpace: 'pre-wrap' }}>{activeReq.notes}</span>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Riwayat Approval / Audit Trail */}
          <Card
            size="small"
            title={
              <Space>
                <CheckCircleOutlined style={{ color: '#7BAA18' }} />
                <span style={{ fontWeight: 600 }}>Jejak Persetujuan (Audit Trail)</span>
              </Space>
            }
            style={{ borderRadius: '8px' }}
          >
            <Descriptions size="small" bordered column={2}>
              <Descriptions.Item label="Sales Agent (Pengaju)">
                {activeReq.submitted_by?.full_name || activeReq.submitted_by_name || lead.assigned_to?.full_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Waktu Diajukan">
                {activeReq.created_at ? dayjs(activeReq.created_at).format('DD MMM YYYY, HH:mm') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Disetujui Oleh (Approver)">
                <strong style={{ color: '#274f13' }}>
                  {activeReq.processed_by?.full_name || 'Manager / Atasan'}
                </strong>
              </Descriptions.Item>
              <Descriptions.Item label="Waktu Disetujui">
                <strong style={{ color: '#274f13' }}>
                  {activeReq.processed_at ? dayjs(activeReq.processed_at).format('DD MMM YYYY, HH:mm') : '-'}
                </strong>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Space>
      )}
    </Modal>
  );
};

export default LeadClosingDetailModal;
